import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  message as antMessage,
  Space,
  DatePicker,
  Divider,
  Spin,
  Row,
  Skeleton,
  Descriptions,
  Button,
  Typography,
  Form,
  Tag,
  Modal,
  Tabs,
  Popover
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import useAuthContext from '../../../contexts/AuthContext';
import useErrorMessage from '../../../utils/ErrorMessage';
import PageHeader from '../../../components/PageHeader/PageHeader';
import ContentCustom from '../../../components/ContentCustom/ContentCustom';
import { useGenerateExcel } from '../../../utils/generateExcel';
import { months } from './utils';

const { TabPane } = Tabs;

const dateFormatMth = 'MM/YYYY';

const MyBrokerCommissions = () => {
  const { t } = useTranslation();
  const { formattedContractCommissionExcel } = useGenerateExcel();
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [remuneration, setRemuneration] = useState();
  const [totalRemuneration, setTotalRemuneration] = useState();
  const [totalMonthRemuneration, setTotalMonthRemuneration] = useState();
  const [certificates, setCertificates] = useState();
  const [totalPaperWork, setTotalPaperWork] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isloading, setIsloading] = useState(false);
  const [uniqueYears, setUniqueYears] = useState([]);

  const { insurer } = user;

  const formattedData = (data) => {
    let sumTotalRemuneration = 0;
    data.forEach((commission) => {
      sumTotalRemuneration += commission.amount;
      setTotalRemuneration(sumTotalRemuneration);
    });
    const years = data.map((d) => moment(d.date).year());
    setUniqueYears([...new Set(years)]);
  };

  const getCommissions = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/commissions/?insurer=${insurer._id}`
      });
      setRemuneration(data.sort((a, b) => new Date(a.date) - new Date(b.date)));
      formattedData(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const onMonthChange = (date) => {
    if (date) {
      const month = moment(date).get('month');
      const year = moment(date).get('year');
      let sumMonthRemuneration = 0;
      remuneration.forEach((commission) => {
        if (
          moment(commission.date).get('month') === month &&
          moment(commission.date).get('year') === year
        ) {
          sumMonthRemuneration += commission.amount;
        }
        setTotalMonthRemuneration(sumMonthRemuneration);
      });
    }
  };

  // Function to calculate the pwf received
  const calculatePaperWork = (data) => {
    let paperWork = 0;
    data.forEach((paperWorkFee) => {
      paperWork += paperWorkFee.amount;
    });
    setTotalPaperWork(paperWork);
  };

  const getPaperworkFee = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/paperWorkFee?insurer=${user?.insurer._id}&paperworkFee_status=PAID`
      });
      calculatePaperWork(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getCertificates = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/certificates?populate=pdf_file&sort=-date`
      });
      setCertificates(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  useEffect(() => {
    if (user?._id) {
      (async () => {
        await getCommissions();
        await getPaperworkFee();
        await getCertificates();
        setIsLoading(false);
      })();
    }
    // eslint-disable-next-line
  }, [user]);

  const printBordereauExcel = async () => {
    setIsVisible(true);
    setDownloaded(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/commissions/contractList?populate=insurer user&insurerId=${user?.insurer._id}&startDate=${startDate}&endDate=${endDate}&status=!QUOTE`
      });
      const total = 0;
      const total_pwf = 0;
      const commission = {
        amount: total,
        // eslint-disable-next-line no-underscore-dangle
        content: data._contracts,
        total_pwf
      };
      const formatData = {
        commission,
        headers: data.headers
      };
      formattedContractCommissionExcel(formatData, 'global');
    } catch (e) {
      setIsError(true);
      if (e.response) message(e.response.status);
    }
    setDownloaded(false);
  };

  const generateFileAndPrintFile = async (id, key, file) => {
    try {
      if (key === 'pdf') {
        setIsloading(true);
        const { data, headers } = await dispatchAPI('GET', {
          url: `/commissions/pdfFile/${id}?fileId=${file?._id}`,
          responseType: 'blob'
        });
        if (key === 'pdf') {
          const blob = new Blob([data], {
            type: `${'application/pdf'}`
          });
          let fileName;
          if (!file) {
            fileName = headers['content-disposition'].substring(
              headers['content-disposition'].indexOf('=') + 1,
              headers['content-disposition'].length
            );
          }
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${file ? file.metadata.originalName : fileName}`;
          a.click();
        }
      } else {
        setIsDownloading(true);
        const { data } = await dispatchAPI('GET', {
          url: `/commissions/excelFile/${id}`
        });
        formattedContractCommissionExcel(data);
      }
      antMessage.success(t('export.messages.success'));
      if (key === 'excel') {
        setIsDownloading(false);
      } else {
        setIsloading(false);
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const downloadContent = (id, pdfFile) => (
    <>
      <Button
        onClick={() => generateFileAndPrintFile(id, 'excel')}
        loading={isDownloading}
      >
        EXCEL
      </Button>
      <Divider orientation="vertical" />
      <Button
        onClick={() => generateFileAndPrintFile(id, 'pdf', pdfFile)}
        loading={isloading}
      >
        PDF
      </Button>
    </>
  );

  const generateTaxCertificate = async (year) => {
    try {
      setIsloading(true);
      const { data, headers } = await dispatchAPI('GET', {
        url: `/insurers/taxCertificate/${insurer._id}?year=${year}`,
        responseType: 'blob'
      });
      const blob = new Blob([data], {
        type: `${'application/pdf'}`
      });
      const fileName = headers['content-disposition'].substring(
        headers['content-disposition'].indexOf('=') + 1,
        headers['content-disposition'].length
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}`;
      a.click();
      antMessage.success(t('export.messages.success'));
      setIsloading(false);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const downloadCertificate = async (file) => {
    const {
      _id,
      metadata: { originalName },
      contentType
    } = file;
    setIsDownloading(true);
    try {
      const response = await dispatchAPI('GET', {
        url: `/certificates/files/${_id}`,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: contentType
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = originalName;
      a.click();
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsDownloading(false);
  };

  return (
    <>
      <Modal
        open={isVisible}
        onOk={() => setIsVisible(false)}
        onCancel={() => {
          setIsVisible(false);
        }}
        footer={null}
      >
        {downloaded ? (
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexFlow: 'column',
              marginTop: 20
            }}
          >
            <Spin />
            <Typography.Text style={{ margin: 20, textAlign: 'center' }}>
              {t('myBrokerCommissions.download.preparation')}
            </Typography.Text>
          </div>
        ) : (
          <>
            {isError ? (
              <Typography.Text style={{ margin: 20, textAlign: 'center' }}>
                {t('myBrokerCommissions.download.error')}
              </Typography.Text>
            ) : (
              <Typography.Text style={{ margin: 20, textAlign: 'center' }}>
                {t('myBrokerCommissions.download.success')}
              </Typography.Text>
            )}
          </>
        )}
      </Modal>
      <PageHeader title={t('insurers.customportal.commissions.title')} />
      <ContentCustom>
        {isLoading ? (
          <Spin />
        ) : (
          <>
            <Typography.Title level={5}>
              {t('myBrokerCommissions.text.titleSlip')}
            </Typography.Title>
            {user && (
              <Descriptions column={1}>
                <Descriptions.Item>
                  <div style={{ display: 'flex', flexFlow: 'column' }}>
                    <p style={{ marginTop: 25 }}>
                      {t('myBrokerCommissions.text.msg6')}
                    </p>
                    <Tabs
                      defaultActiveKey="1"
                      size="large"
                      type="card"
                      tabBarGutter={10}
                    >
                      {uniqueYears &&
                        uniqueYears.map((year) => {
                          const hasDecember = remuneration.some(
                            (r) =>
                              moment(r.date).year() === year &&
                              moment(r.date).month() === 11
                          );
                          return (
                            <TabPane
                              tab={year}
                              key={year}
                              style={{ marginTop: '20px' }}
                            >
                              {hasDecember && (
                                <Button
                                  style={{ margin: 5 }}
                                  loading={isloading}
                                  onClick={() => generateTaxCertificate(year)}
                                >
                                  <DownloadOutlined
                                    style={{ marginRight: 5 }}
                                  />
                                  DADS
                                </Button>
                              )}
                              {remuneration &&
                                remuneration.map((r) => {
                                  if (moment(r.date).year() === year) {
                                    return (
                                      <Popover
                                        content={() =>
                                          downloadContent(r._id, r.pdf_file)
                                        }
                                        title={t('buttons.download')}
                                        trigger="click"
                                        placement="bottom"
                                        key={r._id}
                                      >
                                        <Button style={{ margin: 5 }}>
                                          <Popover
                                            placement="bottom"
                                            content={`Total: ${r.amount.toFixed(
                                              2
                                            )} €`}
                                          >
                                            <DownloadOutlined
                                              style={{ marginRight: 5 }}
                                            />
                                            {
                                              months[
                                                moment(r.date).get('month')
                                              ]
                                            }
                                          </Popover>
                                        </Button>
                                      </Popover>
                                    );
                                  }
                                  return true;
                                })}
                            </TabPane>
                          );
                        })}
                    </Tabs>
                  </div>
                </Descriptions.Item>
              </Descriptions>
            )}
            <Descriptions
              column={1}
              className="commission-remuneration-description"
            >
              <Space>
                <span>
                  {t('myBrokerCommissions.text.msg1')}
                  <DatePicker
                    format={dateFormatMth}
                    picker="month"
                    onChange={onMonthChange}
                    disabledDate={(current) => {
                      return current >= moment();
                    }}
                  />
                  :
                </span>
                <Tag color="geekblue">
                  {` ${totalMonthRemuneration?.toFixed(2) || 0.0} €`}
                </Tag>
              </Space>
              <Divider />
              <Space>
                <span>{t('myBrokerCommissions.text.msg2')}</span>
                <Tag color="geekblue">
                  {` ${totalPaperWork.toFixed(2) || ''}€`}
                </Tag>
              </Space>
              <Divider />
              <Space>
                <span>{t('myBrokerCommissions.text.msg3')}</span>
                <Tag color="geekblue">
                  {` ${totalRemuneration?.toFixed(2) || ''}€`}
                </Tag>
              </Space>
            </Descriptions>
            {user && (
              <>
                <Form
                  layout="horizontal"
                  onFinish={printBordereauExcel}
                  className="commission-bordereau-form"
                >
                  <Descriptions>
                    <div style={{ paddingRight: 16 }}>
                      {t('myBrokerCommissions.text.msg4')}
                    </div>

                    <Form.Item
                      label={t('myBrokerCommissions.text.from')}
                      rules={[{ required: 'date1' }]}
                      name="date1"
                    >
                      <DatePicker
                        format={dateFormatMth}
                        style={{ minWidth: 200 }}
                        onChange={(value) => setStartDate(value)}
                        disabledDate={(current) => {
                          return current >= moment();
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('myBrokerCommissions.text.to')}
                      rules={[{ required: 'date2' }]}
                      name="date2"
                    >
                      <DatePicker
                        format={dateFormatMth}
                        style={{ minWidth: 200 }}
                        onChange={(value) => setEndDate(value)}
                        disabledDate={(current) => {
                          return current >= moment();
                        }}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button htmlType="submit">
                        <DownloadOutlined style={{ fontSize: '14px' }} />
                        {t('myBrokerCommissions.download.button')}
                      </Button>
                    </Form.Item>
                  </Descriptions>
                </Form>
              </>
            )}
          </>
        )}
      </ContentCustom>

      <PageHeader title={t('insurers.customportal.commissions.sub_title')} />
      <ContentCustom>
        {user && (
          <Row gutter={[0, 24]}>
            <Skeleton
              loading={isLoading}
              title={0}
              paragraph={0}
              avatar={{ size: 64 }}
              active
            >
              <Typography.Title level={5} style={{ marginTop: 16 }}>
                Certificats Fiscaux
              </Typography.Title>
              <Descriptions column={1}>
                <Descriptions.Item>
                  <div style={{ display: 'flex', flexFlow: 'column' }}>
                    <p style={{ marginTop: 25 }}>
                      {t('myBrokerCommissions.text.msg7')}
                    </p>
                    {certificates &&
                      certificates.map((r) => {
                        return (
                          <Popover
                            title={t('buttons.download')}
                            trigger="click"
                            placement="bottom"
                          >
                            <Button
                              style={{ margin: 5 }}
                              onClick={() => downloadCertificate(r.pdf_file)}
                            >
                              <Popover
                                placement="bottom"
                                content={`Total: ${r.amount.toFixed(2)} €`}
                              >
                                <DownloadOutlined style={{ marginRight: 5 }} />
                                {moment(r.date).get('year')}
                              </Popover>
                            </Button>
                          </Popover>
                        );
                      })}
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </Skeleton>
          </Row>
        )}
      </ContentCustom>
    </>
  );
};

export default MyBrokerCommissions;
